import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import styles from './Hero.module.css';
import { bannerAPI } from '../../utils/api';
import { Link } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';

const Hero = () => {
  const { theme } = useTheme();
  const [autoPlay, setAutoPlay] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Local fallback banners (Ensure these URLs are high resolution for full-width)
  const fallbackBanners = [
    {
      _id: '1',
      imageUrl: 'https://digitaltopup.in/banners/1745071699852--photo-output.jpeg',
      altText: 'Mobile Legends Banner',
      link: '/games/mobile-legends',
      title: 'Mobile Legends',
      category: 'games',
      isActive: true,
      order: 1
    },
    {
      _id: '2',
      imageUrl: 'https://digitaltopup.in/banners/1742994015836--Blog_MLBB_1.jpg',
      altText: 'Free Fire Banner',
      link: '/games/free-fire',
      title: 'Free Fire',
      category: 'games',
      isActive: true,
      order: 2
    },
    {
      _id: '3',
      imageUrl: 'https://digitaltopup.in/banners/valorant-banner.jpg',
      altText: 'Valorant Banner',
      link: '/games/valorant',
      title: 'Valorant',
      category: 'games',
      isActive: true,
      order: 3
    }
  ];

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await bannerAPI.getPublicBanners();
        setBanners(data?.length > 0 ? data : fallbackBanners);
      } catch (err) {
        setBanners(fallbackBanners);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1 || !autoPlay) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev >= banners.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [banners.length, autoPlay]);

  if (loading) return <div className={styles.loaderFull}>Loading...</div>;

  return (
    <section className={`${styles.heroWrapper} ${theme === 'dark' ? styles.darkTheme : ''}`}>
      {banners.map((banner, index) => (
        <div
          key={banner._id || index}
          className={`${styles.slide} ${index === currentSlide ? styles.activeSlide : ''}`}
        >
          {/* Full Width Background Image */}
          <div
            className={styles.backgroundImage}
            style={{ backgroundImage: `url(${banner.imageUrl})` }}
          />

          {/* Rockstar-style Dark Overlay for Readability */}
          <div className={styles.overlay} />

          {/* Content Overlay */}
          <div className={styles.contentContainer}>
            <div className={styles.textContent}>
              <h4 className={styles.categoryLabel}>{banner.category || 'FEATURED'}</h4>
              <h1 className={styles.mainTitle}>{banner.title}</h1>
              <p className={styles.subTitle}>{banner.subtitle || 'Experience the next generation of gaming.'}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Progress Indicators (Rockstar style dots) */}
      <div className={styles.indicators}>
        <div 
          className={styles.autoPlayController}
          onClick={() => setAutoPlay(!autoPlay)}
        >
          {autoPlay ? <Pause /> : <Play />}
        </div>
        {banners.map((_, i) => (
          <div
            key={i}
            className={`${styles.dotContainer} ${i === currentSlide ? styles.active : ''}`}
            onClick={() => setCurrentSlide(i)}
          >
            {/* The filling bar is only visible/animating when active */}
            <div className={styles.dotFill} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;